// FileViewerRenderer.jsx
// Description: This component renders the file viewer, handling image display, zooming, panning, and tool interactions.

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// External imports
import autoBind from "react-autobind";

// MUI imports
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import withStyles from "@mui/styles/withStyles";

// Icon imports
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

// HSA Imports
import { configureImage, colorInImage } from "../../viewer/utils/RendererUtils";
import { FileViewerTools } from "../FileViewer";
import { trackTransforms, getBaseLog } from "../../viewer/utils/CanvasUtil";
import { withPersistentStorage } from "../../viewer/contexts/PersistentStorageContext";
import { withResultTab } from "../../viewer/contexts/ResultTabContext";
import { withTiles } from "../../viewer/contexts/TilesContext";
import Backend from "../../common/utils/Backend";
import BaseRenderer from "../../viewer/components/BaseRenderer";
import ImageInfo from "../../viewer/components/ImageInfo";
import ImageOverlay from "../../viewer/components/ImageOverlay";
import MiniMap from "../../viewer/components/MiniMap";
import ScaleBar from "../../viewer/components/ScaleBar";
import TimeLineTool from "../../viewer/components/TimeLineTool";
import ZoomBar from "../../viewer/components/ZoomBar";
import ZStackBar from "../../viewer/components/ZStackBar";
import EventBus from "../../common/services/EventBus";
import { getColorMapColor } from "../../viewer/utils/Constants";
import { drawScaleBarFromRenderer } from "../../common/utils/ScaleBarDraw";
import RendererPresence from "../../common/services/RendererPresence";

// debug flags
const FPS = false;

const styles = {
  canvas: {
    position: "relative",
    display: "block",
    width: "100%",
    backgroundColor: "#D3D3D3",
  },

  root: {
    height: "100%",
    position: "relative",
  },

  heatmapContainer: {
    pointerEvents: "none",
    position: "absolute !important",
    zIndex: "9998",
    width: "100%",
    height: "100%",
  },

  toolbarButtonRoot: {
    color: "#666",
    position: "absolute",
    top: 5,
    right: 35,
    zIndex: 100,
  },
  toolbarButtonRootSingle: {
    color: "#666",
    position: "absolute",
    top: 5,
    right: 5,
    zIndex: 100,
  },
  toolbarButton: {
    position: "relative",
    display: "inline-block",

    width: 40,
    height: 40,
    padding: 8,
    margin: 0,
  },
  toolbarButtonIcon: {
    verticalAlign: "-4px",
  },
  toolbarButtonChecked: {
    width: 40,
    color: "#0673C1",
  },
  progress: {
    position: "absolute",
    margin: -20,
    left: "50%",
    top: "50%",
    zIndex: 1000,
  },
  fps: {
    position: "absolute",
    right: 10,
    top: 10,
    color: "white",
    fontSize: 20,
    pointerEvents: "none",
  },
  resizableContainer: {
    "& .react-resizable-handle": {
      pointerEvents: "all",
    },
    "& .react-resizable-handle-se::before": {
      content: "''",
      display: "block",
      position: "absolute",
      bottom: 3,
      right: 3,
      width: 6,
      height: 6,
      borderBottom: "1px solid #0673c1",
      borderRight: "1px solid #0673c1",
    },
  },
  fileNavLeftBtn: {
    position: "absolute",
    left: 5,
    top: "50%",
    marginTop: "-40px",
    "& svg": {
      fontSize: "80px",
    },
  },
  fileNavRightBtn: {
    position: "absolute",
    right: 5,
    top: "50%",
    marginTop: "-40px",
    "& svg": {
      fontSize: "80px",
    },
  },
  slideImagesContainer: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  importProgressContainer: {
    position: "relative",
    width: 400,
    padding: 6,
    boxSizing: "border-box",
  },
  importProgressBar: {
    height: 32,
    borderRadius: 4,
  },
  importProgressText: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFF",
    fontWeight: 700,
    fontSize: 14,
    pointerEvents: "none",
    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
  },
  importOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 2000,
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
    pointerEvents: "none",
  },
  importProgressLabel: {
    fontWeight: 600,
    fontSize: 14,
    margin: "8px 8px 0 8px",
    color: "#333",
    textAlign: "center",
  },
};

const debounce = (func, delay) => {
  let inDebounce;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => func.apply(context, args), delay);
  };
};

class FileViewerRenderer extends BaseRenderer {
  constructor(props) {
    super(props);
    autoBind(this); // bind all methods to this context
    this._isMounted = false;
    // transmit context to componentRef
    if (props.componentRef) props.componentRef(this);

    this.dragStart = null; // used for dragging the canvas
    this.activeZoomValueOut = null;
    this.state = {
      ...this.state, // from BaseRenderer
      isDrawing: false, //stc
      mouseOnCanvas: false,
      // tools
      isBrightfield:
        props.ome &&
        props.ome.channels.length === 1 &&
        props.ome.channels[0].type === "brightfield",
      scaleBarData: null,
      miniMapReady: false,
      open: false,
      anchorEl: null,
      displayTimeBar: false,
      displayZStackBar: false,
      rotState: 0,
      mirrored: false,
    };

    // event listener functions
    this.handleMouseWheel = (e) => this.mousewheel(e);
    this.handleMouseUp = (e) => this.mouseup(e);
    this.handleMouseMove = (e) => this.mousemove(e);
    this.handleTouchStart = (e) => this.touchStart(e);
    this.handleTouchMove = (e) => this.touchMove(e);
    this.handleTouchEnd = (e) => this.touchEnd(e);

    window.getMousePositionInImage = this.getMousePositionInImage;
    window.setZoomLevelForGridSize = this.setZoomLevelForGridSize;

    // frontend cached alpha channel images
    this.visibleImage = [];
    // frontend cached colored images
    this.coloredImages = [];

    // fps history to smooth fps counter update
    this.lastFps = Array.from(Array(10), () => 60);

    this.createBackgroundPattern();

    this.resizeObserver = null;
    this.zoomMoveActionDebounced = debounce(this.zoomMoveAction, 100);
    this.debouncedUpdateHeatmapCanvas = debounce(this.updateHeatmapCanvas, 100);
    this.debouncedSetScaleBarData = debounce((data) => {
      const cur = this.state.scaleBarData;
      if (
        !cur ||
        cur.width !== data.width ||
        cur.label !== data.label ||
        cur.x !== data.x ||
        cur.y !== data.y
      ) {
        if (this._isMounted) this.setMountedState({ scaleBarData: data });
      }
    }, 50);

    // Touch pinch state
    this.isPinching = false;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
    this.pinchCenter = { x: 0, y: 0 };

    // Ensure we restore persisted zoom only after initial initialization (zoomOut)
    // to avoid draw() overwriting our transform while initialized=false.
    this._pendingInitialZoomRestore = true;
  }

  componentDidMount() {
    this.componentDidMountDefault();
    let stateObject = {};
    this.canvas = document.getElementById(this.props.canvasId);
    // get drawing context from canvas
    this.ctx = this.canvas.getContext("2d");

    // bind mouse wheel events
    this.canvas.addEventListener("DOMMouseScroll", this.handleMouseWheel, true);
    this.canvas.addEventListener("mousewheel", this.handleMouseWheel, {
      passive: false,
    });
    window.addEventListener("mouseup", this.handleMouseUp, { passive: true });
    window.addEventListener("mousemove", this.handleMouseMove, {
      passive: true,
    });
    this.canvas.addEventListener("touchstart", this.handleTouchStart, {
      passive: false,
    });
    this.canvas.addEventListener("touchmove", this.handleTouchMove, {
      passive: false,
    });
    this.canvas.addEventListener("touchend", this.handleTouchEnd, {
      passive: false,
    });

    // extend context with some fancy additional transformation methods
    trackTransforms(this.ctx);

    // converts the relative size css information into an absolute size which we can access
    this.canvas.width = this.canvas.offsetWidth;
    this.canvas.height = this.canvas.offsetHeight;

    // initialize position
    stateObject.lastX = this.canvas.width / 2;
    stateObject.lastY = this.canvas.height / 2;

    this.t0 = performance.now();

    stateObject["displayTimeBar"] = this.props.displayTimeBar;
    stateObject["displayZStackBar"] = this.props.displayZStackBar;

    this.resizeObserver = new window.ResizeObserver(
      debounce(() => {
        if (!this._isMounted) return;
        this.handleResize();
      }, 500),
    );
    if (this.container) {
      this.resizeObserver.observe(this.container);
    }

    //set mouse postition to center of canvas
    this.mousePos = this.getMousePositionInImage();
    this.draw();
    // Note: zoom restore is applied in componentDidUpdate once initialized.

    EventBus.on("chainEvent", (data) => this.handleChainEvent(data));

    // Register this fileId as actively rendered in this window
    try {
      RendererPresence.register(this.props.fileId);
    } catch (e) {
      // ignore presence registration errors
    }

    // Initialize rotState from localStorage if available (per fileId)
    try {
      const savedRot = localStorage.getItem(
        "hsaFileViewerRotState" + this.props.fileId,
      );
      const rot = Number.parseInt(savedRot, 10);
      if ([0, 90, 180, 270].includes(rot)) {
        this.setMountedState({ rotState: rot });
      }
    } catch (e) {
      // ignore localStorage errors
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const oldFileId = prevProps.fileId;
    const newFileId = this.props.fileId;

    // Apply persisted zoom *after* the initial zoomOut() initialization ran.
    // This prevents a resize/remount from temporarily restoring zoom and then being
    // overwritten by draw()'s initialization branch.
    if (
      oldFileId === newFileId &&
      this._pendingInitialZoomRestore &&
      !prevState.initialized &&
      this.state.initialized
    ) {
      this._pendingInitialZoomRestore = false;
      setTimeout(() => {
        if (!this._isMounted) return;
        this.initZoomObject();
      }, 0);
    }

    // Update presence registry if fileId changed
    if (oldFileId !== newFileId) {
      try {
        if (oldFileId) RendererPresence.unregister(oldFileId);
        if (newFileId) RendererPresence.register(newFileId);
      } catch (e) {
        // ignore
      }
    }

    if (prevState.heatMapTiles !== this.heatMapTiles) {
      this.debouncedUpdateHeatmapCanvas();
    }

    // on file change => reset renderer cache
    if (oldFileId && oldFileId !== newFileId) {
      this.fileIdStateData[oldFileId] = prevState;

      // For file switches we intentionally do a zoomFit; don't apply mount-restore.
      this._pendingInitialZoomRestore = false;

      if (this.fileIdStateData[newFileId]) {
        this.setMountedState(this.fileIdStateData[newFileId]);
      }

      setTimeout(() => this.zoomFit(), 100);
      this.setMountedState({
        miniMapKey: new Date().getTime(),
      });

      // Apply persisted rotation for the new fileId, if present
      try {
        const savedRot = localStorage.getItem(
          "hsaFileViewerRotState" + newFileId,
        );
        const rot = Number.parseInt(savedRot, 10);
        if ([0, 90, 180, 270].includes(rot)) {
          this.setMountedState({ rotState: rot });
        }
      } catch (e) {
        // ignore
      }
    }
  }

  componentWillUnmount() {
    this.componentWillUnmountDefault();

    // unbind mouse wheel events
    this.canvas.removeEventListener(
      "DOMMouseScroll",
      this.handleMouseWheel,
      true,
    );
    this.canvas.removeEventListener("mousewheel", this.handleMouseWheel, {
      passive: false,
    });
    window.removeEventListener("mouseup", this.handleMouseUp, {
      passive: true,
    });
    window.removeEventListener("mousemove", this.handleMouseMove, {
      passive: true,
    });
    this.canvas.removeEventListener("touchstart", this.handleTouchStart, {
      passive: false,
    });
    this.canvas.removeEventListener("touchmove", this.handleTouchMove, {
      passive: false,
    });
    this.canvas.removeEventListener("touchend", this.handleTouchEnd, {
      passive: false,
    });

    // Clean up ResizeObserver
    if (this.resizeObserver && this.container) {
      this.resizeObserver.unobserve(this.container);
      this.resizeObserver.disconnect();
    }
    this.keepRendering = false;

    EventBus.off("handleChainEvent", this.handleChainEvent);

    // Unregister this fileId from presence registry
    try {
      RendererPresence.unregister(this.props.fileId);
    } catch (e) {
      // ignore
    }
  }

  handleChainEvent(data) {
    if (data.fileId && data.fileId === this.props.fileId) return; // ignore events from own fileId
    switch (data.type) {
      case "chainMouseDrag": {
        const fPt = this.getPointInCanvas({ x: data.lastX, y: data.lastY });
        this.chainMouseDrag(fPt.x, fPt.y);
        break;
      }
      case "chainMouseUp": {
        const moveTo = this.getPointInCanvas({ x: data.lastX, y: data.lastY });
        this.chainMoveTo(moveTo.x, moveTo.y);
        break;
      }
      case "chainMoveTo": {
        if (
          this.miniMapRef === null ||
          typeof this.miniMapRef === "undefined"
        ) {
          return;
        }
        const ctx = this.miniMapRef.getCtx();
        const tP = ctx.transformedPoint(data.x, data.y);
        this.chainMoveTo(tP.x, tP.y);
        break;
      }
      case "chainMouseDown": {
        const fDragStart = this.getPointInCanvas({
          x: data.lastX,
          y: data.lastY,
        });
        this.dragStart = fDragStart;
        break;
      }
      case "chainMousewheel": {
        this.chainMousewheel(data.event, data.leaderZoomPoint);
        this.zoomValueOut = data.zoomValueOut;
        break;
      }
      case "zoomFit": {
        if (data.fileIds.includes(this.props.fileId)) {
          this.zoomFit();
        }
        break;
      }
      case "zoomOneToN": {
        if (data.fileIds.includes(this.props.fileId)) {
          this.zoomOneToN(data.value);
        }
        break;
      }
      case "zoomDelta": {
        if (data.fileIds.includes(this.props.fileId)) {
          this.zoomDelta(data.value);
        }
        break;
      }
    }
    this.draw();
  }

  /**
   * Handles the resize event for the canvas element.
   * Checks if the canvas exists and has a non-zero width.
   * If the displayed width of the canvas differs from its actual width,
   * triggers the updateDimensions prop to adjust the canvas size accordingly.
   */
  handleResize() {
    if (!this.canvas || this.canvas.offsetWidth === 0) return;
    if (this.canvas.offsetWidth !== this.canvas.width) {
      this.props.updateDimensions();
    }
  }

  /**
   * Resets the renderer to its initial state.
   * - Clears all tiles.
   * - Resets frontend-cached alpha channel and colored images.
   * - Resets drag state and playback status.
   * - Reloads all tiles.
   *
   * @todo Adapt for splitscreen mode so that not all frames are reset.
   */
  reset() {
    this.props.tiles.clearTiles();
    this.props.tiles.setVisibleImage([]);
    this.props.tiles.setColoredImages([]);

    this.setMountedState({
      playing: false,
    });
    this.dragStart = null;

    this.loadAllTiles();
  }

  zoomToRect(zoomTop, zoomLeft, zoomRight, zoomBottom) {
    if (!this.canvas || !this.ctx) return;
    if (zoomTop === zoomBottom || zoomLeft === zoomRight) return;

    const left = Math.min(zoomLeft, zoomRight);
    const right = Math.max(zoomLeft, zoomRight);
    const top = Math.min(zoomTop, zoomBottom);
    const bottom = Math.max(zoomTop, zoomBottom);

    const rectWidth = right - left;
    const rectHeight = bottom - top;
    if (rectWidth <= 0 || rectHeight <= 0) return;

    const canvasW = this.canvas.width;
    const canvasH = this.canvas.height;
    if (!canvasW || !canvasH) return;

    // Compute the exact scale so the given rect fits into the current viewport.
    let targetScale = Math.min(canvasW / rectWidth, canvasH / rectHeight);

    // Clamp to allowed zoom range to avoid subtle drift from constraint logic elsewhere.
    if (typeof this.computeMinMaxScale === "function") {
      const { minScale, maxScale } = this.computeMinMaxScale();
      targetScale = Math.max(minScale, Math.min(maxScale, targetScale));
    }

    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;

    // Set scale + translation separately so the zoom level is deterministic.
    const tx = canvasW / 2 - centerX * targetScale;
    const ty = canvasH / 2 - centerY * targetScale;
    this.canvasTransform(targetScale, 0, 0, targetScale, tx, ty);

    this.zoomMoveAction();
    this.draw();
  }

  /**
   * Initializes the zoom object by centering and zooming into a region of interest (ROI)
   * defined by the zoomTop, zoomLeft, zoomRight, and zoomBottom props.
   *
   * - If the ROI is not valid (top equals bottom or left equals right), the function exits.
   * - Otherwise, after a short delay, it calculates the center of the ROI and translates the canvas context
   *   to center the ROI on the screen.
   * - Then, it repeatedly zooms into the ROI as long as it is completely inside the current view.
   */
  initZoomObject() {
    const {
      zoomTop,
      zoomLeft,
      zoomRight,
      zoomBottom,
      zoomScale,
      zoomCenterX,
      zoomCenterY,
    } = this.props;

    // Preferred restore path: keep the exact scale (zoom level) invariant across resizes.
    if (
      typeof zoomScale === "number" &&
      zoomScale > 0 &&
      typeof zoomCenterX === "number" &&
      typeof zoomCenterY === "number" &&
      this.canvas &&
      this.ctx
    ) {
      const canvasW = this.canvas.width;
      const canvasH = this.canvas.height;

      let targetScale = zoomScale;
      if (typeof this.computeMinMaxScale === "function") {
        const { minScale, maxScale } = this.computeMinMaxScale();
        targetScale = Math.max(minScale, Math.min(maxScale, targetScale));
      }

      const tx = canvasW / 2 - zoomCenterX * targetScale;
      const ty = canvasH / 2 - zoomCenterY * targetScale;
      this.canvasTransform(targetScale, 0, 0, targetScale, tx, ty);

      this.draw();
      return;
    }

    // Backward compatibility: restore using the old ROI bounds.
    this.zoomToRect(zoomTop, zoomLeft, zoomRight, zoomBottom);
  }

  /**
   * Calculates the current pointer (mouse or touch) position relative to the image displayed on the canvas,
   * taking into account the canvas position, scaling, and any transformations.
   *
   * @param {Object} [event] - Optional mouse or touch event object. If not provided, uses stored mouse position.
   * @returns {{x: number, y: number}} The pointer position in image coordinates.
   */
  getMousePositionInImage(event) {
    let x, y;

    if (event) {
      // Handle touch event
      if (event.touches) {
        const touch = event.touches[0] || event.changedTouches[0];
        x = touch.clientX;
        y = touch.clientY;
      }
      // Handle mouse event
      else {
        x = event.clientX;
        y = event.clientY;
      }
    } else {
      // Use stored mouse position if no event provided
      x = this.props.getMousePosition()[0];
      y = this.props.getMousePosition()[1];
    }

    this.lastX = x - this.canvas.getBoundingClientRect().left;
    this.lastY = y - this.canvas.getBoundingClientRect().top;

    // Calculate mouse position in image coordinates
    let p1 = this.getPointInCanvas({
      x: this.lastX,
      y: this.lastY,
    });
    let p2 = this.getPointInCanvas({ x: 0, y: 0 });
    let p3 = this.getPosition();

    p1.x += p2.x - p3.x / this.getScale();
    p1.y += p2.y - p3.y / this.getScale();

    return p1;
  }

  /**
   * Handles mouse events and forwards them to the active tool's mouse handler.
   *
   * - Clamps the mouse position within the viewer's width (`vw`) and height (`vh`).
   * - Constructs a mouse event object with relevant context.
   * - Calls the current tool's `mouse` method with the constructed object.
   * - If the active tool is `POLYMANIPULATION` and a `polyToolRef` exists, also forwards the event to its `mouse` method.
   *
   * @param {Object} event - The mouse event object from the UI.
   * @returns {*} The result of the tool's mouse handler, or `undefined` if no handler is present.
   */
  toolMouseForward(event) {
    const { FV } = this.props;
    if (!FV.tool || !FV.tool.mouse) {
      return;
    }
    let p1 = this.mousePos;
    p1.x = p1.x < 0 ? 0 : p1.x;
    p1.x = p1.x > this.vw ? this.vw : p1.x;
    p1.y = p1.y < 0 ? 0 : p1.y;
    p1.y = p1.y > this.vh ? this.vh : p1.y;

    let mouseObject = {
      event: event,
      p: p1,
      fullyLoaded: false,
      vw: this.vw,
      vh: this.vh,
      scale: this.getScale(),
      renderer: this,
    };

    let selection = FV.tool.mouse(mouseObject);

    return selection;
  }

  /**
   * Handles the mouse down event on the file viewer canvas.
   *
   * - Initiates drawing or dragging actions based on the mouse button pressed and the current tool state.
   * - If a drawing tool is active and the mouse is within the image bounds, forwards the event to the tool handler.
   * - If the middle mouse button or left mouse button (with no active tool) is pressed, starts a drag operation for panning.
   * - Handles chained viewers and synchronizes drag start positions across chained or fullscreen viewers.
   * - Supports temporary chain toggling when the Alt key is held during mouse down.
   * - Prevents default event propagation for handled cases.
   *
   * @param {MouseEvent} event - The mouse down event object.
   */
  mousedown(event) {
    const { FV } = this.props;
    if (FV.activeFileId !== this.props.fileId) {
      FV.setActiveFileId(this.props.fileId);
    }
    let isDrawing = false;
    let p1 = this.getMousePositionInImage();
    this.mousePos = p1;
    this.props.tiles.chainLeaderId = this.props.canvasId;

    if (FV.activeTool && !event.ctrlKey) {
      // only drawing on image is allowed
      if (p1.x >= 0 && p1.x <= this.vw && p1.y >= 0 && p1.y <= this.vh) {
        this.toolMouseForward(event);
      }
    }

    const isDrawingTool = FV.activeTool === FileViewerTools.Comment;

    // start dragging if middle mouse button is down or no tool selected
    if (event.button === 1 || (event.button === 0 && !isDrawingTool)) {
      // Always allow middle mouse button (1) for panning
      if (!this.dragROI && !this.resizeROI) {
        this.dragStart = this.getPointInCanvas({
          x: this.lastX,
          y: this.lastY,
        });
        this.canvas.style.cursor = "move";
      } else {
        isDrawing = true;
        this.setMountedState({
          isDrawing: true,
        });
      }

      if (this.props.isChained) {
        EventBus.emit("chainEvent", {
          type: "chainMouseDown",
          fileId: this.props.fileId,
          lastX: this.lastX,
          lastY: this.lastY,
        });
      }

      //remove
      if ((event.button === 0 || event.button === 1) && event.altKey) {
        this.tempChainToggle = true;

        if (this.props.showFullscreen) {
          console.log("fsChain");
        } else {
          this.props.onChangeChain(
            false,
            this.props.splitscreenIdx,
            this.props.fileId,
          );
        }
      }

      // dont forward any event upwards
      event.preventDefault();
      return false;
    } else {
      if (!isDrawing) {
        this.setMountedState({ isDrawing: true });
      }
    }
  }

  /**
   * Handles mouse movement events on the canvas, updating internal state and performing
   * actions such as drawing, ROI (Region of Interest) dragging/resizing, and canvas panning.
   * Also manages chained canvas interactions for synchronized views.
   *
   * @param {MouseEvent} event - The mousemove event object.
   */
  mousemove(event) {
    const { FV } = this.props;
    //event
    let p1 = this.getMousePositionInImage(event);
    // Always update mousePos so overlays (e.g., streamed cursor) track pointer
    this.mousePos = p1;
    // Only forward tool mouse events when actively drawing or the pointer is over the canvas
    // Note: BaseRenderer sets `this.mouseOnCanvas` (instance field) in canvas event handlers
    if (this.state.isDrawing || this.mouseOnCanvas) {
      if (FV.activeTool && FV.tool) {
        this.toolMouseForward(event);
      }
    }
    if (this.dragROI) {
      // handle roi dragging
      this.dragROI.drag(p1, this.vw, this.vh);
    } else if (this.resizeROI) {
      // resize the roi if inside the image
      this.resizeROI.resize(p1, this.vw, this.vh);
    } else if (
      this.dragStart &&
      this.props.tiles.chainLeaderId === this.props.canvasId
    ) {
      // handle canvas dragging
      let pt = this.getPointInCanvas({ x: this.lastX, y: this.lastY });
      // translate canvas
      this.ctx.translate(pt.x - this.dragStart.x, pt.y - this.dragStart.y);
      if (this.props.isChained) {
        EventBus.emit("chainEvent", {
          type: "chainMouseDrag",
          fileId: this.props.fileId,
          lastX: this.lastX,
          lastY: this.lastY,
        });
      }
      this.zoomMoveAction();
    }
  }

  /**
   * Handles touch start events on the canvas, initializing drawing or dragging actions.
   * Processes single touch interactions and determines whether to initiate tool actions or canvas dragging.
   * Handles chain synchronization for linked viewers.
   *
   * @param {TouchEvent} event - The touch start event object containing touch coordinates and event data.
   */
  touchStart(event) {
    const { FV } = this.props;

    if (event.touches.length === 2) {
      // Begin pinch gesture
      const rect = this.canvas.getBoundingClientRect();
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      this.pinchStartDistance = Math.hypot(dx, dy) || 1;
      this.pinchStartScale = this.getScale();
      // Center in canvas local coords
      const cx = (t1.clientX + t2.clientX) / 2 - rect.left;
      const cy = (t1.clientY + t2.clientY) / 2 - rect.top;
      this.pinchCenter = { x: cx, y: cy };
      this.zoomPoint = { x: cx, y: cy };
      this.props.tiles.chainLeaderId = this.props.canvasId;
      this.isPinching = true;
      // Prevent default browser zoom
      event.preventDefault();
      return;
    }

    if (event.touches.length === 1) {
      const touch = event.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.lastX = touch.clientX - rect.left;
      this.lastY = touch.clientY - rect.top;

      let p1 = this.getMousePositionInImage(event);
      this.mousePos = p1;
      this.props.tiles.chainLeaderId = this.props.canvasId;

      if (
        FV.activeTool &&
        FV.activeTool !== FileViewerTools.None &&
        !event.ctrlKey
      ) {
        if (p1.x >= 0 && p1.x <= this.vw && p1.y >= 0 && p1.y <= this.vh) {
          this.setMountedState({ isDrawing: true });
          this.toolMouseForward(
            this.convertTouchToMouseEvent(event, "mousedown"),
          );
        }
      } else if (!this.dragROI && !this.resizeROI) {
        this.dragStart = this.getPointInCanvas({
          x: this.lastX,
          y: this.lastY,
        });
        this.canvas.style.cursor = "move";

        if (this.props.isChained) {
          EventBus.emit("chainEvent", {
            type: "chainMouseDown",
            fileId: this.props.fileId,
            lastX: this.lastX,
            lastY: this.lastY,
          });
        }
      }

      event.preventDefault();
    }
  }

  /**
   * Handles touch move events on the canvas, updating tool actions or canvas position.
   * Processes continuous touch movement to update drawing tools or perform canvas panning.
   * Maintains chain synchronization for linked viewers during movement.
   *
   * @param {TouchEvent} event - The touch move event object containing updated touch coordinates.
   */
  touchMove(event) {
    if (event.touches.length === 2 && this.isPinching) {
      // Handle pinch zoom
      const rect = this.canvas.getBoundingClientRect();
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy) || 1;
      const ratio = dist / this.pinchStartDistance;

      // Compute desired zoom with clamping
      let newZoom = this.pinchStartScale * ratio;
      const { minScale, maxScale } = this.computeMinMaxScale();
      newZoom = Math.max(minScale, Math.min(maxScale, newZoom));

      // Update zoom point to current center
      const cx = (t1.clientX + t2.clientX) / 2 - rect.left;
      const cy = (t1.clientY + t2.clientY) / 2 - rect.top;
      this.zoomPoint = { x: cx, y: cy };

      // Apply scaling around zoomPoint immediately for responsiveness
      const pt = this.getPointInCanvas({
        x: this.zoomPoint.x,
        y: this.zoomPoint.y,
      });
      const current = this.getScale();
      if (current > 0 && newZoom > 0 && Math.abs(newZoom - current) > 1e-6) {
        const factor = newZoom / current;
        this.ctx.translate(pt.x, pt.y);
        this.ctx.scale(factor, factor);
        this.ctx.translate(-pt.x, -pt.y);
        // Inform dependent UI/state
        this.zoomMoveAction();
      }

      event.preventDefault();
      return;
    }

    if (event.touches.length === 1) {
      const touch = event.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.lastX = touch.clientX - rect.left;
      this.lastY = touch.clientY - rect.top;

      let p1 = this.getMousePositionInImage(event);
      this.mousePos = p1;

      const { FV } = this.props;

      if (
        FV.activeTool &&
        FV.activeTool !== FileViewerTools.None &&
        this.state.isDrawing
      ) {
        this.toolMouseForward(
          this.convertTouchToMouseEvent(event, "mousemove"),
        );
      } else if (
        this.dragStart &&
        this.props.tiles.chainLeaderId === this.props.canvasId
      ) {
        let pt = this.getPointInCanvas({ x: this.lastX, y: this.lastY });
        this.ctx.translate(pt.x - this.dragStart.x, pt.y - this.dragStart.y);

        if (this.props.isChained) {
          EventBus.emit("chainEvent", {
            type: "chainMouseDrag",
            fileId: this.props.fileId,
            lastX: this.lastX,
            lastY: this.lastY,
          });
        }
        this.zoomMoveAction();
      }

      event.preventDefault();
    }
  }

  /**
   * Handles touch end events on the canvas, completing drawing or dragging actions.
   * Cleans up state and finalizes any ongoing touch interactions.
   *
   * @param {TouchEvent} event - The touch end event object marking the end of touch interaction.
   */
  touchEnd(event) {
    const { FV } = this.props;

    if (
      FV.activeTool &&
      FV.activeTool !== FileViewerTools.None &&
      this.state.isDrawing
    ) {
      this.toolMouseForward(this.convertTouchToMouseEvent(event, "mouseup"));
      this.setMountedState({ isDrawing: false });
    }

    // End pinch
    if (this.isPinching && event.touches.length < 2) {
      this.isPinching = false;
      this.pinchStartDistance = 0;
      this.pinchStartScale = this.getScale();
    }

    if (this.dragStart) {
      this.dragStart = null;
      this.canvas.style.cursor = "default";
    }

    event.preventDefault();
  }

  /**
   * Converts a touch event to a simulated mouse event for compatibility with existing tool handlers.
   * Creates a synthetic mouse event with relevant properties mapped from the touch event.
   *
   * @param {TouchEvent} touchEvent - The original touch event to convert.
   * @param {string} mouseEventType - The type of mouse event to simulate ('mousedown', 'mousemove', or 'mouseup').
   * @returns {Object} A simulated mouse event object with properties matching a standard MouseEvent.
   */
  convertTouchToMouseEvent(touchEvent, mouseEventType) {
    const touch = touchEvent.touches[0] || touchEvent.changedTouches[0];
    return {
      type: mouseEventType,
      button: 0,
      clientX: touch ? touch.clientX : 0,
      clientY: touch ? touch.clientY : 0,
      preventDefault: touchEvent.preventDefault.bind(touchEvent),
      stopPropagation: touchEvent.stopPropagation.bind(touchEvent),
      target: touchEvent.target,
      currentTarget: touchEvent.currentTarget,
    };
  }

  isChained() {
    for (const chainFileId of this.props.chainListFileIds) {
      if (chainFileId === this.props.fileId) {
        return true;
      }
    }
    return false;
  }

  chainMouseMove(x, y) {
    if (this.props.isChained) {
      EventBus.emit("chainEvent", {
        type: "chainMoveTo",
        fileId: this.props.fileId,
        x: x,
        y: y,
      });
    }
  }

  chainMouseDrag(x, y) {
    if (!this.dragStart) {
      return;
    }
    if (this.props.tiles.chainLeaderId !== this.props.canvasId) {
      if (this.props.isChained) {
        // translate canvas
        this.ctx.translate(x - this.dragStart.x, y - this.dragStart.y);
      }
    }
  }

  mouseEnter() {
    this.setMountedState({ mouseOnCanvas: true });
  }

  mouseLeave() {
    this.setMountedState({ mouseOnCanvas: false });
  }

  mouseup(event) {
    const { FV } = this.props;
    // event
    let p1 = this.getMousePositionInImage(event);
    this.mousePos = p1;

    if (FV.activeTool && this.state.isDrawing) {
      this.toolMouseForward(event);
    }

    // stop dragging the canvas
    if (this.dragStart) {
      this.dragStart = null;
      this.canvas.style.cursor = "default";
    }

    // stop dragging a roi
    if (this.dragROI) {
      this.dragROI.handleMouseUp();
      this.dragROI = null;
    }

    //remove
    if (this.tempChainToggle) {
      this.tempChainToggle = false;
      if (this.props.showFullscreen) {
        console.log("fsChain");
      } else {
        this.props.onChangeChain(
          true,
          this.props.splitscreenIdx,
          this.props.fileId,
        );
      }
    }

    // update view
    if (!this._isMounted) return;
    this.forceUpdate();
  }

  computeMinMaxScale() {
    // Guard against uninitialized dimensions
    if (!this.vw || !this.vh || this.vw <= 0 || this.vh <= 0) {
      return { minScale: 0.1, maxScale: 64 };
    }

    let minScale = (0.75 * this.canvas.height) / this.vh;

    if ((0.75 * this.canvas.width) / this.vw < minScale) {
      minScale = (0.75 * this.canvas.width) / this.vw;
    }

    if (
      this.props.isActive === false &&
      this.props.isChained &&
      this.activeZoomValueOut
    ) {
      minScale = this.activeZoomValueOut;
    }

    const maxScale = 64; // Maximum zoom-in level
    return { minScale, maxScale };
  }

  mousewheel(event) {
    const { FV } = this.props;
    if (FV.activeFileId !== this.props.fileId) {
      FV.setActiveFileId(this.props.fileId);
    }
    // save zoom destination point
    this.props.tiles.chainLeaderId = this.props.canvasId;
    if (this.state.initialized && !event.ctrlKey) {
      this.zoomPoint = {
        x: this.lastX,
        y: this.lastY,
      };
      this.onScaleOnly(event);
    }

    if (this.props.isChained) {
      EventBus.emit("chainEvent", {
        type: "chainMousewheel",
        fileId: this.props.fileId,
        event: event,
        leaderZoomPoint: this.zoomPoint,
        zoomValueOut: this.zoomValueOut,
      });
    }
    event.preventDefault();
  }

  chainMousewheel(event, leaderZoomPoint) {
    // save zoom destination point
    if (this.props.tiles.chainLeaderId !== this.props.canvasId) {
      if (this.props.isChained) {
        if (
          this.state.initialized &&
          !event.ctrlKey &&
          !event.shiftKey
          // &&!this.props.resultTab.getZoomLevelFixed()
        ) {
          this.zoomPoint = leaderZoomPoint;
          this.onScaleOnly(event);
        }
      }
      event.preventDefault();
    } else return;
  }

  miniMapZoom(event, x, y) {
    this.props.tiles.chainLeaderId = this.props.canvasId;
    if (this.state.initialized && !event.ctrlKey) {
      let ctx = this.miniMapRef.getCtx();
      let tpt = ctx.transformedPoint(x, y);
      let pt = {
        x: tpt.x * this.ctx.getTransform().a + this.ctx.getTransform().e,
        y: tpt.y * this.ctx.getTransform().d + this.ctx.getTransform().f,
      };
      pt.x = Math.round(pt.x);
      pt.y = Math.round(pt.y);
      if (
        pt.x < this.canvas.width &&
        pt.x > 0 &&
        pt.y < this.canvas.height &&
        pt.y > 0
      ) {
        this.zoomPoint = pt;
      } else {
        return;
      }
      this.onScaleOnly(event);
    }

    if (this.props.isChained) {
      let l = this.props.showFullscreen
        ? this.props.splitscreenFileIds
        : this.props.chainListFileIds;
      for (const value of l) {
        if (value !== this.props.fileId) {
          let r = this.props.rendererDict[value];
          let c = r.miniMapRef.getCtx();
          let p = c.transformedPoint(x, y);
          let pt = {
            x: p.x * r.ctx.getTransform().a + r.ctx.getTransform().e,
            y: p.y * r.ctx.getTransform().d + r.ctx.getTransform().f,
          };
          pt.x = Math.round(pt.x);
          pt.y = Math.round(pt.y);
          if (
            pt.x < r.canvas.width &&
            pt.x > 0 &&
            pt.y < r.canvas.height &&
            pt.y > 0
          ) {
            r.zoomPoint = pt;
          } else {
            return;
          }
          r.chainMousewheel(event, pt);
        }
      }
    }
    event.preventDefault();
  }

  zoomOut() {
    this.defaultZoomOut();
  }

  pushColoredImage(c, tileId, visImg) {
    const channel = this.props.histogramConfig.channels[c];
    if (
      (this.props.histogramConfig.channels.length < 2 &&
        channel.color === "#ffffff") ||
      channel.color === -1
    ) {
      // rgb channel
      if (!this.props.tiles.getColoredImage(tileId)) {
        const colImg = configureImage(visImg, channel);
        this.props.tiles.pushColoredImages(colImg, tileId);
      }
    } else {
      // colored images cache
      if (!this.props.tiles.getColoredImage(tileId) && visImg.width > 0) {
        const colImg = colorInImage(visImg, channel);
        this.props.tiles.pushColoredImages(colImg, tileId);
      }
    }
  }

  loadAllTiles() {
    for (let lv = 0; lv < Math.min(2, this.props.ome.maxLevel + 1); lv++) {
      // calculate the number of grid columns for this level
      let cols = Math.pow(2, lv);
      for (let x = 0; x < cols; x++) {
        for (let y = 0; y < cols; y++) {
          for (let c = 0; c < this.props.ome.channels.length; c++) {
            let page = this.getPageForChannel(c);

            const { x: rx, y: ry } = this.getRotatedTileCoords(x, y, cols);
            // load image first if missing
            let tileId =
              page + "," + lv + "," + rx + "," + ry + "," + this.props.fileId;
            if (!this.props.tiles.getVisibleImage(tileId)) {
              let params = {
                id: this.props.fileId,
                page: page,
                lv: lv,
                x: rx,
                y: ry,
                rotState: this.state.rotState,
                mirrored: this.state.mirrored,
              };
              let visImg = new Image();
              visImg.src = Backend.renderRegion(params);
              visImg.onload = () => {
                this.pushColoredImage(c, tileId, visImg);
                this.props.tiles.pushVisibleImage(visImg, tileId);
                // set context width and height
                this.props.tiles.setImgWidth(visImg.width);
                this.props.tiles.setImgHeight(visImg.height);
              };
            } else if (this.props.tiles.getVisibleImage(tileId).complete) {
              // set context width and height
              let img = this.props.tiles.getVisibleImage(tileId);
              this.props.tiles.setImgWidth(img.width);
              this.props.tiles.setImgHeight(img.height);
              this.pushColoredImage(
                c,
                tileId,
                this.props.tiles.getVisibleImage(tileId),
              );
            }
          }
        }
      }
    }

    this.preloadNextFrames();
    this.props.tiles.setHistogramConfig(this.props.histogramConfig);
  }

  preloadFrame(z, t) {
    for (let lv = 0; lv < 1; lv++) {
      // calculate the number of grid columns for this level
      let cols = Math.pow(2, lv);
      for (let x = 0; x < cols; x++) {
        for (let y = 0; y < cols; y++) {
          for (let c = 0; c < this.props.ome.channels.length; c++) {
            let page = this.getPage(c, z, t);
            const { x: rx, y: ry } = this.getRotatedTileCoords(x, y, cols);
            // load image first if missing
            let tileId =
              page + "," + lv + "," + rx + "," + ry + "," + this.props.fileId;
            if (!this.props.tiles.getVisibleImage(tileId)) {
              let visImg = new Image();
              const params = {
                id: this.props.fileId,
                page: page,
                lv: lv,
                x: rx,
                y: ry,
                rotState: this.state.rotState,
                mirrored: this.state.mirrored,
              };
              visImg.src = Backend.renderRegion(params);
              visImg.onload = () => {
                this.pushColoredImage(c, tileId, visImg);
              };
            } else if (this.props.tiles.getVisibleImage(tileId).complete) {
              this.pushColoredImage(
                c,
                tileId,
                this.props.tiles.getVisibleImage(tileId),
              );
            }
          }
        }
      }
    }
  }

  zoomOriginal() {
    this.zoomOneToN(1);
  }

  zoomOneToN(x) {
    this.zoomOneToX(x);
  }

  zoomDelta(delta) {
    this.zoomDeltaDefault(delta);
  }

  zoomFit() {
    this.canvasTransform(1, 0, 0, 1, 0, 0);
    this.zoomOut();
    this.zoomMoveAction();
  }

  /**
   * Performs a context.setTransform, whilst ensuring that the pixel
   * representation is according to zoomlevel
   * @param {*} a Horizontal scaling. A value of 1 results in no scaling.
   * @param {*} b Vertical skewing.
   * @param {*} c Horizontal skewing.
   * @param {*} d Vertical scaling. A value of 1 results in no scaling.
   * @param {*} e Horizontal translation (moving).
   * @param {*} f Vertical translation (moving).
   */
  canvasTransform(a, b, c, d, e, f) {
    // disable smoothing to see pixels of image
    if (this.getScale() > 5) {
      this.ctx.imageSmoothingEnabled = false;
    } else {
      this.ctx.imageSmoothingEnabled = true;
    }
    // Actual transformation
    this.ctx.setTransform(a, b, c, d, e, f);
  }

  // triggered by moving or zooming
  zoomMoveAction() {
    const p1 = this.getPointInCanvas({ x: 0, y: 0 });
    const p2 = this.getPointInCanvas({
      x: this.canvas.width,
      y: this.canvas.height,
    });
    const scale = this.getScale();
    const safeScale = scale && Number.isFinite(scale) ? scale : 1;
    const centerX = (this.getPosition().x + this.canvas.width / 2) / safeScale;
    const centerY = (this.getPosition().y + this.canvas.height / 2) / safeScale;
    // save current zoom and position for reload
    const zoomObject = {
      zoomRoi: true,
      zoomLeft: p1.x,
      zoomRight: p1.x + p2.x - p1.x,
      zoomTop: p1.y,
      zoomBottom: p1.y + p2.y - p1.y,
      zoomScale: safeScale,
      zoomCenterX: centerX,
      zoomCenterY: centerY,
    };
    if (!this.props.showFullscreen) {
      this.props.persistentStorage.save(
        "zoomObjectFV" + this.props.fileId,
        zoomObject,
      );
    }
  }

  updateZ(z, minZ = this.state.minZ, maxZ = this.state.maxZ) {
    this.setMountedState({ z: z, minZ: minZ, maxZ: maxZ });
    this.props.updateGlobalZ(z, minZ, maxZ);
  }

  updateT(t) {
    this.setMountedState({ t: t });
    this.props.updateGlobalT(t);
  }

  /**
   * Calculates the coordinates of a tile after applying horizontal mirroring and rotation.
   *
   * @param {number} x - The original x-coordinate of the tile.
   * @param {number} y - The original y-coordinate of the tile.
   * @param {number} cols - The total number of columns in the grid.
   * @returns {{x: number, y: number}} The new coordinates after mirroring and rotation.
   *
   * The transformation is based on the component's state:
   * - If `this.state.mirrored` is true, the tile is mirrored horizontally.
   * - The rotation is determined by `this.state.rotState` (0, 90, 180, or 270 degrees).
   */
  getRotatedTileCoords(x, y, cols) {
    let mx = this.state.mirrored ? cols - 1 - x : x;
    let my = y;
    switch (this.state.rotState) {
      case 90:
        return { x: my, y: cols - 1 - mx };
      case 180:
        return { x: cols - 1 - mx, y: cols - 1 - my };
      case 270:
        return { x: cols - 1 - my, y: mx };
      default:
        return { x: mx, y: my };
    }
  }

  /**
   * Calculates the parent tile's coordinates and the offset of a child tile within its parent,
   * taking into account mirroring and rotation states.
   *
   * @param {number} x - The x-coordinate of the child tile.
   * @param {number} y - The y-coordinate of the child tile.
   * @param {number} factor - The scaling factor between parent and child tiles (e.g., 2 for 2x2).
   * @param {number} parentCols - The number of columns in the parent tile grid.
   * @returns {{ parentX: number, parentY: number, offsetX: number, offsetY: number }} An object containing:
   *   - parentX: The x-coordinate of the parent tile in the rotated space.
   *   - parentY: The y-coordinate of the parent tile in the rotated space.
   *   - offsetX: The x-offset of the child tile within the parent tile, in the rotated space.
   *   - offsetY: The y-offset of the child tile within the parent tile, in the rotated space.
   */
  getParentTileInfo(x, y, factor, parentCols) {
    // Apply mirroring to child coordinates before calculating parent
    let mx = this.state.mirrored ? parentCols * factor - 1 - x : x;
    let my = y;

    // Get the parent tile's coordinates in the rotated space
    const { x: parentX, y: parentY } = this.getRotatedTileCoords(
      Math.floor(mx / factor),
      Math.floor(my / factor),
      parentCols,
    );

    // Now, get the offset of the child tile within the parent tile, in the rotated space
    // This is the tricky part: the offset depends on the rotation and mirroring
    let offsetX, offsetY;
    switch (this.state.rotState) {
      case 90:
        offsetX = my % factor;
        offsetY = factor - 1 - (mx % factor);
        break;
      case 180:
        offsetX = factor - 1 - (mx % factor);
        offsetY = factor - 1 - (my % factor);
        break;
      case 270:
        offsetX = factor - 1 - (my % factor);
        offsetY = mx % factor;
        break;
      default:
        offsetX = mx % factor;
        offsetY = my % factor;
        break;
    }

    return { parentX, parentY, offsetX, offsetY };
  }

  updateHeatmapCanvas() {
    if (
      this.props.FV.activeTool !== FileViewerTools.ViewHeatMap ||
      !this.heatMapTiles ||
      !Array.isArray(this.heatMapTiles) ||
      this.heatMapTiles.length === 0
    ) {
      this.heatmapCanvas = null;
      return;
    }
    if (!this.heatmapCanvas) {
      this.heatmapCanvas = document.createElement("canvas");
    }
    this.heatmapCanvas.width = this.canvas.width;
    this.heatmapCanvas.height = this.canvas.height;
    const ctx = this.heatmapCanvas.getContext("2d");
    this.drawHeatmapOverlay(ctx);
  }

  /**
   * Draws a heatmap overlay on the provided temporary canvas context.
   *
   * This method visualizes heatmap data by drawing blurred circles (hot spots) for each tile,
   * interpolating between tile levels, and applying a colormap with adjustable alpha transparency.
   * It uses an offscreen buffer for accumulation and handles mirroring and rotation transformations.
   *
   * @param {CanvasRenderingContext2D} tempCtx - The temporary canvas 2D context to draw the heatmap overlay onto.
   */
  drawHeatmapOverlay(tempCtx) {
    const { FV } = this.props;
    if (FV.activeTool !== FileViewerTools.ViewHeatMap) return;
    if (!this.heatMapTiles || !Array.isArray(this.heatMapTiles)) return;

    // const colormapType = colormaps[this.state.colormapIdx]; // Use a consistent colormap type
    const colormapType = "jet"; // Default colormap type
    const colorMapAlpha = this.props.FV.heatMapOpacity / 100;
    // Create an offscreen buffer for accumulation
    const bufferCanvas = document.createElement("canvas");
    bufferCanvas.width = tempCtx.canvas.width;
    bufferCanvas.height = tempCtx.canvas.height;
    const bufferCtx = bufferCanvas.getContext("2d");

    // Group tiles by level
    const tilesByLevel = {};
    this.heatMapTiles.forEach((tile) => {
      if (!tilesByLevel[tile.level]) tilesByLevel[tile.level] = [];
      tilesByLevel[tile.level].push(tile);
    });

    // Find the highest fully populated level
    let maxFullLevel = -1;
    Object.entries(tilesByLevel).forEach(([levelStr, tiles]) => {
      const level = parseInt(levelStr, 10);
      const expectedCount = Math.pow(2, level) * Math.pow(2, level);
      if (tiles.length === expectedCount && level > maxFullLevel) {
        maxFullLevel = level;
      }
    });

    // Only use tiles from levels higher than maxFullLevel
    const filteredTiles = this.heatMapTiles
      .filter((t) => t.level > maxFullLevel)
      .sort((a, b) => a.level - b.level);

    // Find the max level for normalization
    const thresholdLevel = 4; // or whatever value you want

    const maxLevel =
      filteredTiles.length > 0
        ? Math.max(...filteredTiles.map((t) => t.level))
        : 1;

    // Use threshold for normalization if maxLevel is lower
    const normDenominator = Math.max(maxLevel, thresholdLevel);

    filteredTiles.forEach((tile) => {
      const { x, y, level } = tile;
      const tilesPerSide = Math.pow(2, level);
      const norm = normDenominator > 0 ? level / normDenominator : 0;

      bufferCtx.save();
      if (this.state.mirrored) {
        bufferCtx.scale(-1, 1);
        bufferCtx.translate(-bufferCanvas.width, 0);
      }
      bufferCtx.rotate(this.state.rotState * (Math.PI / 180));

      // Calculate tile position and size
      const tileWidth = bufferCanvas.width / tilesPerSide;
      const tileHeight = bufferCanvas.height / tilesPerSide;
      const tileX = x * tileWidth;
      const tileY = y * tileHeight;

      // For outer rectangles (lower levels), use more blur and lower alpha
      const isOuterLevel = level <= thresholdLevel; // or: level < maxLevel
      const blurAmount = isOuterLevel ? "blur(16px)" : "blur(5px)";
      const alpha = isOuterLevel ? colorMapAlpha * 0.5 : colorMapAlpha;

      bufferCtx.save();
      bufferCtx.globalAlpha = alpha;
      bufferCtx.filter = blurAmount;
      bufferCtx.fillStyle = getColorMapColor(colormapType, norm, alpha);

      // Optionally, draw as a circle for smoother edges
      if (isOuterLevel) {
        const centerX = tileX + tileWidth / 2;
        const centerY = tileY + tileHeight / 2;
        const radius = Math.max(tileWidth, tileHeight) * 0.7;
        bufferCtx.beginPath();
        bufferCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        bufferCtx.fill();
      } else {
        bufferCtx.fillRect(tileX, tileY, tileWidth, tileHeight);
      }

      bufferCtx.filter = "none";
      bufferCtx.restore();
    });

    // Draw the accumulated buffer to the main tempCtx
    tempCtx.save();
    tempCtx.globalAlpha = 1.0;
    tempCtx.drawImage(bufferCanvas, 0, 0);
    tempCtx.restore();
  }

  drawVisibleImage() {
    // calc deltatime for animations and fps
    let t1 = performance.now();
    let dt = t1 - this.t0;
    this.t0 = t1;

    // calc smoothed fps
    this.lastFps.pop();
    this.lastFps = [1000 / dt, ...this.lastFps];
    this.fps = Math.round(
      this.lastFps.reduce((acc, val) => (acc += val)) / this.lastFps.length,
    );
    if (FPS) {
      this.setMountedState({
        fps: this.fps,
      });
    }

    // time playback
    if (this.state.playing) {
      let newT =
        this.state.t + (dt / 60000) * this.state.sr * this.state.playDirection;
      if (newT >= this.props.ome.sizeT) {
        newT = 0;
      } else if (newT < 0) {
        newT = this.props.ome.sizeT - 0.001;
      }
      this.updateT(newT);
      this.preloadNextFrames();
    }

    // time playback
    if (this.state.playingZ) {
      let newZ =
        this.state.z +
        (dt / 60000) * this.state.zsr * this.state.playDirectionZ;
      if (newZ > this.props.ome.sizeZ - 1) {
        newZ = 0;
      } else if (newZ < 0) {
        newZ = this.props.ome.sizeZ - 1;
      } else {
        newZ = newZ < 0 ? this.props.ome.sizeZ - 1 : newZ;
      }
      this.updateZ(newZ);
      this.preloadNextZFrames();
    }

    // nothing to draw if we haven't loaded our meta data yet
    if ((!this.props.ome || !this.canvas) && this.keepRendering) {
      // request next animation frame
      requestAnimationFrame(() => this.draw());
      return;
    }

    // call the initialization if the viewer is not initalized yet
    if (!this.state.initialized) {
      // do we have our top region already loaded? (then -> this.vw is greater 0)
      if (this.imgLoaded && this.imgRegistered) {
        // do initial placement
        this.zoomOut();
      } else {
        this.loadAllTiles();
      }
    }

    // only set z once
    if (!this.state.initializedZ) {
      let z = this.props.persistentStorage.load("z");
      if (typeof z === "undefined") {
        z = Math.floor(this.props.ome.sizeZ / 2);
      }
      let minZ = this.props.persistentStorage.load("minZ");
      if (typeof minZ === "undefined") {
        minZ = 0;
      }
      let maxZ = this.props.persistentStorage.load("maxZ");
      if (typeof maxZ === "undefined") {
        maxZ = this.props.ome.sizeZ - 1;
      }
      this.updateZ(z, minZ, maxZ);
      this.props.tiles.setZLevel(Math.floor(this.props.ome.sizeZ / 2));
      this.setMountedState({
        minZ,
        maxZ,
        initializedZ: true,
      });
    }

    if (this.zoomTo) {
      if (typeof this.zoomPoint === "undefined") {
        this.zoomPoint = { x: this.lastX, y: this.lastY };
      }
      if (this.zoomDirection > 0 && this.getScale() < this.zoomTo) {
        // zoom in
        // calc our transformation offset
        let pt = this.getPointInCanvas({
          x: this.zoomPoint.x,
          y: this.zoomPoint.y,
        });

        this.ctx.translate(pt.x, pt.y);
        // calc scale factor (zoom per second)
        let factor = Math.pow(20, 40 / 1000);
        // perform scaling
        this.ctx.scale(factor, factor);
        if (this.getScale() > this.zoomTo) {
          this.canvasTransform(
            this.zoomTo,
            this.ctx.getTransform().b,
            this.ctx.getTransform().c,
            this.zoomTo,
            this.ctx.getTransform().e,
            this.ctx.getTransform().f,
          );
        }
        // restore our offset
        this.ctx.translate(-pt.x, -pt.y);
        // update screen, update scalebar etc
        this.zoomMoveAction();
      } else if (this.zoomDirection < 0 && this.getScale() > this.zoomTo) {
        // zoom out
        // calc our transformation offset
        let pt = this.getPointInCanvas({
          x: this.zoomPoint.x,
          y: this.zoomPoint.y,
        });
        this.ctx.translate(pt.x, pt.y);
        // calc scale factor (zoom per second)
        let factor = Math.pow(20, -40 / 1000);
        // perform scaling
        this.ctx.scale(factor, factor);

        if (this.getScale() < this.zoomTo) {
          this.canvasTransform(
            this.zoomTo,
            this.ctx.getTransform().b,
            this.ctx.getTransform().c,
            this.zoomTo,
            this.ctx.getTransform().e,
            this.ctx.getTransform().f,
          );
        }
        // restore our offset
        this.ctx.translate(-pt.x, -pt.y);
        // update screen, update scalebar etc

        // this.zoomMoveActionDebounced();
        this.zoomMoveAction();
      } else {
        // we reached the destination zoom level
        if (this.zoomTo !== null) {
          this.zoomTo = null;
          this.forceUpdate(); // to trigger a re-render
        }
      }
    }

    this.trackAllViewedRegions();

    // get world size from ome metadata
    this.vw = this.props.ome.sizeX;
    this.vh = this.props.ome.sizeY;

    if (this.state.rotState === 90 || this.state.rotState === 270) {
      this.vw = this.props.ome.sizeY;
      this.vh = this.props.ome.sizeX;
    }

    // Clear the entire canvas
    let p1 = this.getPointInCanvas({ x: 0, y: 0 });
    let p2 = this.getPointInCanvas({
      x: this.canvas.width,
      y: this.canvas.height,
    });
    this.ctx.clearRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
    this.ctx.save();
    this.canvasTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.restore();

    this.ctx.fillStyle = "#000";
    this.ctx.fillRect(0, 0, this.vw, this.vh);

    // get pyramid level based on scaling
    this.level = getBaseLog(2, (this.getScale() / this.state.initialScale) * 2);
    // clip our level at lower bound
    if (this.level < 0 || this.level === Infinity) {
      this.level = 0;
    }
    // clip our level at upper bound
    if (this.level > this.props.ome.maxLevel) {
      this.level = this.props.ome.maxLevel;
    }

    let lv = Math.floor(this.level);

    // calculate the number of grid columns for this level
    let cols = Math.pow(2, lv);

    // get visbile column indices
    let visX = [];
    for (let i = 0; i < cols; i++) {
      if (
        this.getPosition().x / this.getScale() < this.vw * ((i + 1) / cols) &&
        (this.getPosition().x + this.canvas.width) / this.getScale() >
          this.vw * (i / cols)
      ) {
        visX.push(i);
      }
    }
    // out of bounds, add first region
    if (visX.length === 0) visX.push(0);

    // get visbile row indices
    let visY = [];
    for (let i = 0; i < cols; i++) {
      if (
        this.getPosition().y / this.getScale() < this.vh * ((i + 1) / cols) &&
        (this.getPosition().y + this.canvas.height) / this.getScale() >
          this.vh * (i / cols)
      ) {
        visY.push(i);
      }
    }
    // out of bounds, add first region
    if (visY.length === 0) visY.push(0);

    for (let x of visX) {
      for (let y of visY) {
        for (let c = 0; c < this.props.histogramConfig.channels.length; c++) {
          let page = this.getPageForChannel(c);
          let channel = this.props.histogramConfig.channels[c];

          // skip disabled channels
          if (!channel.enabled) continue;
          // file +tile id im string
          // load image first if missing
          const { x: rx, y: ry } = this.getRotatedTileCoords(x, y, cols);
          let tileId =
            page + "," + lv + "," + rx + "," + ry + "," + this.props.fileId;
          if (
            this.zoomTo == null &&
            !this.props.tiles.getVisibleImage(tileId) &&
            !this.state.isLoadingTiles &&
            !this.props.changingFile
          ) {
            let visImg = new Image();
            const params = {
              id: this.props.fileId,
              page: page,
              lv: lv,
              x: rx,
              y: ry,
              rotState: this.state.rotState,
              mirrored: this.state.mirrored,
            };
            visImg.src = Backend.renderRegion(params);
            this.props.tiles.pushVisibleImage(visImg, tileId);
          }

          let img = this.props.tiles.getVisibleImage(tileId);
          this.imgLoaded = img && img.complete;
          // see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
          const compositionModeFluor = "screen";
          const compositionModeBrightfield = "lighter";
          if (this.imgLoaded && this.imgRegistered) {
            // image is loaded -> draw it
            this.pushColoredImage(c, tileId, img);

            if (
              tileId.includes(this.props.activeFileId) &&
              (this.props.tiles.getImgWidth() !== img.width ||
                this.props.tiles.getImgHeight() !== img.height)
            ) {
              // set context width and height (for splitscreen and gallery)
              this.props.tiles.setImgWidth(img.width);
              this.props.tiles.setImgHeight(img.height);
            }

            // composition should be screen for fluorescence
            if (this.props.ome.channels[c].type === "fluorescence") {
              this.ctx.globalCompositeOperation = compositionModeFluor;
            } else {
              this.ctx.globalCompositeOperation = compositionModeBrightfield;
            }
            if (
              Object.prototype.toString.call(
                this.props.tiles.getColoredImage(tileId),
              ) === "[object HTMLCanvasElement]" &&
              this.props.tiles.getColoredImage(tileId)
            ) {
              // Save context state
              this.ctx.save();

              // Calculate tile position and size
              const tileX = (x * this.vw) / cols;
              const tileY = (y * this.vh) / cols;
              const tileW = this.vw / cols;
              const tileH = this.vh / cols;

              // Move to the center of the tile
              this.ctx.translate(tileX + tileW / 2, tileY + tileH / 2);

              let drawW = tileW;
              let drawH = tileH;

              // --- MIRRORING ---
              if (this.state.mirrored) {
                this.ctx.scale(-1, 1); // Mirror horizontally
              }

              // Rotate according to rotState and swap width/height if needed
              switch (this.state.rotState) {
                case 90:
                  this.ctx.rotate((90 * Math.PI) / 180);
                  drawW = tileH;
                  drawH = tileW;
                  // rotated = true;
                  break;
                case 180:
                  this.ctx.rotate((180 * Math.PI) / 180);
                  break;
                case 270:
                  this.ctx.rotate((270 * Math.PI) / 180);
                  drawW = tileH;
                  drawH = tileW;
                  // rotated = true;
                  break;
                default:
                  // no rotation
                  break;
              }

              // Draw the image centered at (0,0) after rotation
              this.ctx.drawImage(
                this.props.tiles.getColoredImage(tileId),
                -drawW / 2,
                -drawH / 2,
                drawW,
                drawH,
              );

              // Restore context state
              this.ctx.restore();
            }
          } else if (!this.state.mirrored) {
            // draw image of previous pyramid level instead
            let factor = 1;
            let parentlv = lv;
            while (parentlv > 0) {
              parentlv--;
              factor *= 2;
              let parentCols = Math.pow(2, parentlv);

              // Use the helper to get parent tile and offset in parent tile
              const {
                parentX: prx,
                parentY: pry,
                offsetX,
                offsetY,
              } = this.getParentTileInfo(x, y, factor, parentCols);

              // Adjust the offset for mirrored state
              const realOffsetX = this.state.mirrored
                ? factor - 1 - offsetX // Fully reverse the horizontal order
                : offsetX; // Keep the original offset for non-mirrored

              tileId =
                page +
                "," +
                parentlv +
                "," +
                prx +
                "," +
                pry +
                "," +
                this.props.fileId;

              let imgOld = this.props.tiles.getVisibleImage(tileId);

              if (imgOld && imgOld.complete) {
                this.pushColoredImage(c, tileId, imgOld);

                // composition should be screen for fluorescence
                if (this.props.ome.channels[c].type === "fluorescence") {
                  this.ctx.globalCompositeOperation = compositionModeFluor;
                } else {
                  this.ctx.globalCompositeOperation =
                    compositionModeBrightfield;
                }

                if (this.props.tiles.getColoredImage(tileId)) {
                  // Save context state
                  this.ctx.save();

                  // Calculate tile position and size (destination)
                  const tileX = (x * this.vw) / cols;
                  const tileY = (y * this.vh) / cols;
                  const tileW = this.vw / cols;
                  const tileH = this.vh / cols;

                  // Move to the center of the tile
                  this.ctx.translate(tileX + tileW / 2, tileY + tileH / 2);

                  let drawW = tileW;
                  let drawH = tileH;

                  // --- ROTATION ---
                  switch (this.state.rotState) {
                    case 90:
                      this.ctx.rotate((90 * Math.PI) / 180);
                      drawW = tileH;
                      drawH = tileW;
                      break;
                    case 180:
                      this.ctx.rotate((180 * Math.PI) / 180);
                      break;
                    case 270:
                      this.ctx.rotate((270 * Math.PI) / 180);
                      drawW = tileH;
                      drawH = tileW;
                      break;
                    default:
                      // no rotation
                      break;
                  }

                  // Calculate source position and size in the parent tile
                  let srcW = imgOld.width / factor;
                  let srcH = imgOld.height / factor;
                  let srcX = realOffsetX * srcW;
                  let srcY = offsetY * srcH;

                  // Draw the image
                  this.ctx.drawImage(
                    this.props.tiles.getColoredImage(tileId),
                    srcX,
                    srcY,
                    srcW,
                    srcH,
                    -drawW / 2,
                    -drawH / 2,
                    drawW,
                    drawH,
                  );

                  // Restore context state
                  this.ctx.restore();
                }
                break;
              }
            }
          }
        }
      }
    }
    // back to default overlay composition operation
    // see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
    this.ctx.globalCompositeOperation = "source-over";
  }

  draw() {
    const { FV } = this.props;
    this.drawVisibleImage();
    let p2 = this.getPointInCanvas({
      x: this.canvas.width,
      y: this.canvas.height,
    });

    this.ctx.globalAlpha = 1.0;
    // draw scale bar (suppressed if tempHideScaleBar is active)
    if (
      FV.layoutStateObject.scaleBar &&
      !FV.tempHideScaleBar &&
      this.state.scaleBarData
    ) {
      drawScaleBarFromRenderer(this.ctx, this.state.scaleBarData, {
        p2,
        isBrightfield: this.state.isBrightfield,
      });
    }

    // check if tiles are still loading from server
    let isLoadingTiles = false;
    for (let tileId in this.props.tiles.getVisibleImages()) {
      if (this.props.tiles.getVisibleImage(tileId).complete === false) {
        isLoadingTiles = true;
        break;
      }
    }
    if (isLoadingTiles !== this.state.isLoadingTiles) {
      this.setMountedState({
        isLoadingTiles: isLoadingTiles,
      });
    }
    const commentLayer = Object.prototype.hasOwnProperty.call(
      FV.commentLayer,
      this.props.fileId,
    )
      ? FV.commentLayer[this.props.fileId]
      : null;
    if (FV.activeTool === FileViewerTools.Comment) {
      this.drawComments(commentLayer);
    }
    if (FV.tool && FV.tool.drawCustomCursor) {
      this.ctx.lineWidth = 1 / this.ctx.getTransform().a;
      FV.tool.drawCustomCursor(
        this.ctx,
        this.mousePos,
        this.getScale(),
        this.canvas,
      );
    }
    // Also allow a renderer-level custom cursor (e.g., for streaming overlays)
    if (typeof this.drawCustomCursor === "function") {
      const tr = this.ctx.getTransform();
      const pixelScale = Math.hypot(tr.a, tr.b) || 1; // scale magnitude (handles rotation)
      this.ctx.lineWidth = 1 / pixelScale;
      try {
        this.drawCustomCursor(this.ctx, this.mousePos, pixelScale, this.canvas);
      } catch (e) {
        // ignore drawing errors to avoid breaking the render loop
      }
    }

    // Draw cached heatmap canvas if available
    if (
      this.props.FV.activeTool === FileViewerTools.ViewHeatMap &&
      this.heatmapCanvas
    ) {
      this.ctx.save();
      this.ctx.globalAlpha = 0.8;
      this.ctx.drawImage(
        this.heatmapCanvas,
        0,
        0,
        this.heatmapCanvas.width,
        this.heatmapCanvas.height, // source
        0,
        0,
        this.vw,
        this.vh, // destination
      );
      this.ctx.restore();
    }

    // request next animation frame
    if (this.keepRendering) {
      requestAnimationFrame(() => this.draw());
    }
  }

  setZoomLevelForGridSize(gridSize) {
    // make initial zoomlevel
    this.zoomFit();
    // zoom in that tile is size of viewer
    this.ctx.scale(gridSize, gridSize);
  }

  updateFLChannels(e) {
    this.props.tiles.setHistogramConfig(e);

    this.setMountedState({ histogramConfig: e });

    // invalidate all cached coloring images
    //this.coloredImages = [];
    this.props.tiles.setColoredImages([]);

    this.loadAllTiles();
  }

  render() {
    const { classes, fileId, FV } = this.props; // project

    const { miniMapKey } = this.state;

    return (
      <div className={classes.root} ref={(el) => (this.container = el)}>
        {this.renderCanvas()}

        {this.state.isLoadingTiles && (
          <LinearProgress
            style={{
              position: "fixed",
              top: 64,
              left: 0,
              width: this.container?.offsetWidth || "100%",
            }}
          />
        )}
        {FPS && (
          <p style={{ color: "#0673C1" }} className={classes.fps}>
            {this.state.fps + " FPS"}
          </p>
        )}
        {!this.state.initialized && (
          <CircularProgress className={classes.progress} />
        )}

        {/* Import overlay (normal import only; rotation uses dialog) */}
        {FV &&
          FV.activeFileId === fileId &&
          !FV.processingRotation &&
          typeof FV.importProgress === "number" &&
          FV.importProgress > -1 &&
          FV.importProgress <= 100 && (
            <div className={classes.importOverlay}>
              <div className={classes.importProgressLabel}>
                Importing file:
                <br />
                {FV.activeFileName || "Current file"}
              </div>
              <div className={classes.importProgressContainer}>
                <LinearProgress
                  variant="determinate"
                  value={FV.importProgress}
                  className={classes.importProgressBar}
                  sx={{ "& .MuiLinearProgress-bar": { borderRadius: 4 } }}
                />
                <div className={classes.importProgressText}>
                  {(() => {
                    const raw = Number(FV.importProgress || 0);
                    return raw + "%";
                  })()}
                </div>
              </div>
            </div>
          )}

        {this.state.initialized && FV.layoutStateObject["miniMap"] && (
          <MiniMap
            componentRef={(c) => (this.miniMapRef = c)}
            fileId={fileId}
            key={miniMapKey}
            pointerEvents={!(this.canvas.style.cursor !== "default")}
            ome={this.props.ome}
            histogramConfig={this.props.histogramConfig}
            visibleImage={this.props.tiles.getVisibleImages()}
            coloredImages={this.props.tiles.getColoredImages()}
            position={this.getPosition()}
            zoom={this.getScale()}
            canvas={this.canvas}
            canvasId={this.props.canvasId}
            onMoveTo={this.moveTo.bind(this)}
            onScaleOnly={this.onScaleOnly.bind(this)}
            getPageForChannel={this.getPageForChannel.bind(this)}
            chainMouseMove={this.chainMouseMove.bind(this)}
            zoomMouseWheel={this.miniMapZoom.bind(this)}
            rotState={this.state.rotState}
            mirrored={this.state.mirrored}
            heatMapTiles={this.heatMapTiles}
          />
        )}

        {this.state.initialized &&
          (FV.layoutStateObject.slideLabel ||
            FV.layoutStateObject.macroImage) && (
            <div
              className={classes.slideImagesContainer}
              style={{
                marginTop:
                  this.props.splitscreenCount !== 1 &&
                  !this.props.showFullscreen
                    ? "30px"
                    : "0px",
              }}
            >
              {FV.layoutStateObject.slideLabel && (
                <ImageOverlay
                  imageUrl={Backend.renderSlideLabel(this.props.fileId)}
                />
              )}
              {FV.layoutStateObject.macroImage && (
                <ImageOverlay
                  imageUrl={Backend.renderMacroImage(
                    this.props.fileId,
                    "vertical",
                  )}
                  buttonsBelow={true}
                />
              )}
            </div>
          )}

        {this.state.initialized &&
          this.props.ome &&
          this.props.ome.physicalSizeX !== 1 &&
          FV.layoutStateObject.zoomBar && (
            <ZoomBar
              key={fileId + "ZoomBar"}
              ome={this.props.ome}
              zoom={this.getScale()}
              changeValue={(v) => this.zoomToFactor(v)}
              clickable={true}
              height={Math.min(300, this.container?.offsetHeight * 0.4)}
            />
          )}
        {this.state.initialized && FV.layoutStateObject.scaleBar && (
          <div style={{ display: FV.tempHideScaleBar ? "none" : undefined }}>
            <ScaleBar
              key={fileId + "ScaleBar"}
              pointerEvents={!(this.canvas.style.cursor !== "default")}
              zoom={this.getScale()}
              ome={this.props.ome}
              setScaleBarData={(data) =>
                data && this.debouncedSetScaleBarData(data)
              }
            />
          </div>
        )}

        {this.state.initialized &&
          FV.layoutStateObject["timeBar"] &&
          this.props.ome.sizeT > 1 && (
            <TimeLineTool
              key={fileId + "TimeLineTool"}
              selectedLayer={this.props.selectedLayer}
              structures={this.props.structures}
              frameArray={this.props.frameArrayDict[this.props.fileId]}
              time={this.state.t}
              playing={this.state.playing}
              onPlayPause={this.onPlayPause}
              onSeek={this.onSeek}
              onChangeT={this.onChangeT}
              ome={this.props.ome}
              sr={this.state.sr}
              onChangeSr={this.onChangeSr}
              playDirection={this.state.playDirection}
              onStep={this.onStep}
              resizeTimeLine={this.props.resizeTimeLine}
              timeLineHeight={this.props.timeLineHeight}
            />
          )}
        {this.state.initialized &&
          FV.layoutStateObject.ZStackBar &&
          this.props.ome.sizeZ > 1 && (
            <ZStackBar
              key={fileId + "ZStackBar"}
              pointerEvents={!(this.canvas.style.cursor !== "default")}
              z={this.state.z}
              minZ={this.state.minZ}
              maxZ={this.state.maxZ}
              playing={this.state.playingZ}
              onPlayPause={this.onPlayPauseZ}
              zsr={this.state.zsr}
              onChangeZ={this.onChangeZ}
              onChangeZValue={this.onChangeZValue}
              onChangeZSr={this.onChangeZSr}
              playDirection={this.state.playDirectionZ}
              ome={this.props.ome}
              onStep={this.onStepZ}
            />
          )}
        {this.state.initialized && FV.layoutStateObject.imageInfo && (
          <ImageInfo ome={this.props.ome} rotState={this.state.rotState} />
        )}

        {FV.layoutStateObject["fileNavButtons"] &&
          this.props.splitscreenCount === 1 && (
            <React.Fragment>
              <div className={classes.fileNavLeftBtn}>
                <Tooltip
                  disableInteractive
                  placement="top"
                  followCursor
                  title="Open previous file"
                >
                  <span>
                    <IconButton
                      disabled={FV.sceneDataList[0]?.id === fileId}
                      onClick={() => {
                        this.props.openPrevFile();
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>

              <div className={classes.fileNavRightBtn}>
                <Tooltip
                  disableInteractive
                  placement="top"
                  followCursor
                  title="Open next file"
                >
                  <span>
                    <IconButton
                      disabled={
                        this.props.FV.sceneDataList.at(-1)?.id === fileId
                      }
                      onClick={() => {
                        this.props.openNextFile();
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </React.Fragment>
          )}
      </div>
    );
  }
}

// define the component's interface
FileViewerRenderer.propTypes = {
  activeFileId: PropTypes.string,
  canvasId: PropTypes.string,
  chainListFileIds: PropTypes.array,
  changingFile: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  componentRef: PropTypes.func,
  displayTimeBar: PropTypes.bool,
  displayZStackBar: PropTypes.bool,
  fileId: PropTypes.string,
  histogramConfig: PropTypes.object,
  isActive: PropTypes.bool,
  isChained: PropTypes.bool,
  ome: PropTypes.object,
  onChangeChain: PropTypes.func,
  openNextFile: PropTypes.func,
  openPrevFile: PropTypes.func,
  persistentStorage: PropTypes.object,
  rendererDict: PropTypes.object,
  rightSpace: PropTypes.number,
  showFullscreen: PropTypes.bool,
  splitscreenCount: PropTypes.number,
  splitscreenFileIds: PropTypes.array,
  splitscreenIdx: PropTypes.number,
  tiles: PropTypes.object,
  tzoomROI1: PropTypes.func,
  updateGlobalT: PropTypes.func,
  updateGlobalZ: PropTypes.func,
  zoomBottom: PropTypes.number,
  zoomLeft: PropTypes.number,
  zoomROI: PropTypes.bool,
  zoomRight: PropTypes.number,
  zoomTop: PropTypes.number,
  zoomScale: PropTypes.number,
  zoomCenterX: PropTypes.number,
  zoomCenterY: PropTypes.number,
  getMousePosition: PropTypes.func,
  structures: PropTypes.array,
  selectedLayer: PropTypes.number,
  frameArrayDict: PropTypes.object,
  timeLineHeight: PropTypes.number,
  resizeTimeLine: PropTypes.func,
  updateDimensions: PropTypes.func,
  FV: PropTypes.object, // FileViewer object
};

export default withPersistentStorage(
  withTiles(withResultTab(withStyles(styles)(FileViewerRenderer))),
);
